import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import relativeTime from "dayjs/plugin/relativeTime";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);
dayjs.extend(customParseFormat);

export const APP_TZ = "Asia/Manila";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
export const MONTH_OPTIONS = MONTHS.map((label, i) => ({
  value: i + 1,
  label,
}));

/** ISO UTC timestamp → "6:05 PM" in Manila time. */
export const formatTime = (iso) =>
  iso ? dayjs(iso).tz(APP_TZ).format("h:mm A") : "—";

/** ISO UTC timestamp → "Oct 1, 2026 10:00 AM" in Manila time. */
export const formatDateTime = (iso) =>
  iso ? dayjs(iso).tz(APP_TZ).format("MMM D, YYYY h:mm A") : "—";

/** ISO timestamp → "3 hours ago". */
export const fromNow = (iso) => (iso ? dayjs(iso).fromNow() : "—");

/**
 * Plain "YYYY-MM-DD" date → formatted string. Parsed as a calendar date (no
 * timezone conversion), so the day never shifts.
 */
export const formatPlainDate = (ymd, format = "MMM D, YYYY") =>
  ymd ? dayjs(ymd, "YYYY-MM-DD", true).format(format) : "—";

/** year + month (1-12) → "September 2026". */
export const formatPeriod = (year, month) =>
  year && month ? `${MONTHS[month - 1]} ${year}` : "—";

/** Hours between two ISO timestamps, or null. */
export const hoursBetween = (startIso, endIso) => {
  if (!startIso || !endIso) return null;
  const hours = dayjs(endIso).diff(dayjs(startIso), "minute") / 60;
  return Number.isFinite(hours) ? hours : null;
};

/** Number-ish value (API may send "8.00") → "8.00" or "—". */
export const formatHours = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  return Number.isFinite(n) ? n.toFixed(2) : "—";
};

/** Current year/month in Manila time. */
export const currentPeriod = () => {
  const now = dayjs().tz(APP_TZ);
  return { year: now.year(), month: now.month() + 1 };
};

/** Today's date in Manila as a plain "YYYY-MM-DD" string. */
export const todayInManila = () => dayjs().tz(APP_TZ).format("YYYY-MM-DD");

/** Add days to a plain "YYYY-MM-DD" date (no timezone involved). */
export const addDaysToPlainDate = (ymd, days) =>
  dayjs(ymd, "YYYY-MM-DD", true).add(days, "day").format("YYYY-MM-DD");

/** Whole days from today (Manila) until a plain date; negative = in the past. */
export const daysUntilPlainDate = (ymd) =>
  ymd
    ? dayjs(ymd, "YYYY-MM-DD", true).diff(
        dayjs(todayInManila(), "YYYY-MM-DD", true),
        "day",
      )
    : null;

export default dayjs;
